import React, { useEffect, useState, useCallback } from "react";
import { LifeBuoy, Bell, X, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { fmtDateGB } from "../../lib/format";
import MarginTrend from "../../components/MarginTrend";

const fmtGbp = (v) => v == null ? "—" : `£${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const STATUS = {
  open: "#2563EB", in_progress: "#B45309", waiting: "#7C3AED", resolved: "#15934F", closed: "#64748B",
};
const PRIO = { low: "#64748B", medium: "#2563EB", high: "#B45309", critical: "#C0392B" };
const SEV = { info: "#64748B", warning: "#B45309", critical: "#C0392B" };
const STATUS_OPTS = ["open", "in_progress", "waiting", "resolved", "closed"];

export default function ProjectOpex({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/opex`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load OPEX");
    } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (t, status) => {
    try { await api.patch(`/opex/tickets/${t.id}`, { status }); load(); }
    catch { toast.error("Update failed"); }
  };
  const dismiss = async (a) => { try { await api.post(`/opex/alerts/${a.id}/dismiss`); load(); } catch { toast.error("Failed"); } };

  if (loading) return <div className="muted text-sm">Loading…</div>;
  if (!data) return null;

  const marginColor = data.net_margin_gbp >= 0 ? "#15934F" : "#C0392B";

  return (
    <section className="space-y-6" data-testid="project-opex-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="label flex items-center gap-2"><LifeBuoy size={12} /> OPEX · operations & expenditure</div>
        <Link to="/opex" className="btn-ghost text-xs" data-testid="project-opex-open"><ExternalLink size={12} /> Open OPEX hub</Link>
      </div>

      {/* Spend vs income */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="swiss-card-flat p-4"><div className="text-[10px] uppercase tracking-[0.12em] font-bold muted">Ticket spend</div><div className="font-display text-xl font-bold mt-1 text-[#2563EB]">{fmtGbp(data.ticket_spend_gbp)}</div></div>
        <div className="swiss-card-flat p-4"><div className="text-[10px] uppercase tracking-[0.12em] font-bold muted">Maintenance spend</div><div className="font-display text-xl font-bold mt-1 text-[#B45309]">{fmtGbp(data.maintenance_spend_gbp)}</div></div>
        <div className="swiss-card-flat p-4"><div className="text-[10px] uppercase tracking-[0.12em] font-bold muted">Recorded income</div><div className="font-display text-xl font-bold mt-1 text-[#15934F]">{fmtGbp(data.income_gbp)}</div></div>
        <div className="rounded-lg border p-4" style={{ borderColor: marginColor + "55", background: marginColor + "11" }} data-testid="project-opex-margin">
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: marginColor }}>Net operating margin</div>
          <div className="font-display text-xl font-bold mt-1" style={{ color: marginColor }}>{fmtGbp(data.net_margin_gbp)}</div>
        </div>
      </div>

      {/* Margin trend */}
      <MarginTrend projectId={projectId} months={12} title="Net operating margin trend" />

      {/* Open tickets */}
      <div className="swiss-card p-5">
        <div className="label mb-3">Open tickets · {data.open_count}</div>
        {data.open_tickets.length === 0 ? (
          <div className="muted text-sm py-3">No open tickets for this site.</div>
        ) : (
          <div className="space-y-2">
            {data.open_tickets.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-2.5 rounded border border-[var(--line)]" data-testid={`project-ticket-${t.id}`}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: PRIO[t.priority] || "#64748B" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{t.title}</div>
                  <div className="text-[10px] muted capitalize">{t.category}{t.cost_gbp ? ` · ${fmtGbp(t.cost_gbp)}` : ""}{t.due_date ? ` · due ${fmtDateGB(t.due_date)}` : ""}</div>
                </div>
                <select value={t.status} onChange={(e) => setStatus(t, e.target.value)} className="text-[10px] font-bold rounded px-1.5 py-1 border-0 cursor-pointer" style={{ background: (STATUS[t.status] || "#64748B") + "22", color: STATUS[t.status] || "#64748B" }} data-testid={`project-ticket-status-${t.id}`}>
                  {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alerts */}
      <div className="swiss-card p-5">
        <div className="label mb-3 flex items-center gap-2"><Bell size={12} /> Active alerts · {data.alert_count}</div>
        {data.alerts.length === 0 ? (
          <div className="muted text-sm py-3">No active alerts for this site.</div>
        ) : (
          <div className="space-y-2">
            {data.alerts.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-2.5 rounded border border-[var(--line)]" data-testid={`project-alert-${a.id}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: SEV[a.severity] || "#64748B" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{a.message}</div>
                  <div className="text-[10px] muted">{a.source_platform || a.source}{a.code ? ` · ${a.code}` : ""} · {new Date(a.received_at).toLocaleDateString()}</div>
                </div>
                <button onClick={() => dismiss(a)} className="btn-ghost p-1 text-[#C0392B]" title="Dismiss" data-testid={`project-alert-dismiss-${a.id}`}><X size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
